import {
  Container,
  Flex,
  Text,
  Heading,
  Grid,
  IconButton,
  Box,
  Button,
} from '@radix-ui/themes';
import { Separator } from 'radix-ui';
import { ExternalLinkIcon, ArrowUpIcon, ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/junk-rescue/contexts/use-tenant-context';
import StorageImage from '@trashlab/sitesapp/src/organizations/junk-rescue/components/shared/storage-image';
import { Link } from '@trashlab/sitesapp/src/organizations/junk-rescue/components/shared/link';
import {
  IconBrandFacebook,
  IconBrandX,
  IconBrandYoutube,
  IconBrandInstagram,
  IconMail,
} from '@tabler/icons-react';

// Custom Yelp icon SVG component
const IconYelp = ({ color = 'currentColor', size = 24 }: { color?: string; size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
  >
    <path d="M12.14 11.18L10.08 8.21C9.61 7.54 9.26 6.96 9.26 6.96C8.68 6.04 8.5 5.22 8.76 4.63C9 4.08 9.64 3.75 10.54 3.75H10.58C11.19 3.75 13.41 4.18 14.32 4.5C14.75 4.65 15.04 4.92 15.14 5.27C15.24 5.62 15.14 6 14.87 6.35C14.87 6.35 12.73 9.3 12.14 11.18ZM11.39 13.15C11.06 12.85 10.88 12.43 10.91 12C10.94 11.57 11.18 11.18 11.56 10.94L14.63 9C15.33 8.55 16.46 8.67 16.82 9.29C17.04 9.67 17.5 10.89 17.74 11.82C17.87 12.31 17.77 12.74 17.48 13.03C17.19 13.32 16.74 13.43 16.24 13.32C16.24 13.32 12.58 12.5 11.39 13.15ZM10.91 14.5C10.55 14.76 10.34 15.18 10.34 15.63C10.34 16.08 10.55 16.5 10.91 16.76L13.16 18.39C13.66 18.75 14.15 18.89 14.56 18.77C15 18.64 15.29 18.26 15.39 17.72C15.39 17.72 15.77 15.55 15.77 14.23C15.77 13.69 15.54 13.28 15.13 13.09C14.5 12.81 13.54 13.26 13.54 13.26L10.91 14.5ZM9.5 14.89C9.16 14.73 8.77 14.73 8.43 14.89L5.43 16.26C4.76 16.57 4.47 17.26 4.68 17.83C4.82 18.21 5.32 19.26 5.77 20.03C6.03 20.48 6.41 20.73 6.84 20.73C7.27 20.73 7.66 20.5 7.92 20.07L9.93 16.65C10.19 16.22 10.2 15.7 9.95 15.27C9.82 15.06 9.68 14.96 9.5 14.89ZM8.47 13.11C8.76 12.78 8.87 12.34 8.76 11.92C8.65 11.5 8.34 11.16 7.93 11L4.53 9.65C3.82 9.37 3.07 9.59 2.82 10.18C2.65 10.58 2.5 11.76 2.5 12.75C2.5 13.28 2.68 13.71 3 13.96C3.35 14.23 3.82 14.27 4.33 14.08L8 12.88C8.17 12.97 8.33 12.96 8.47 13.11Z"/>
  </svg>
);
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/junk-rescue/lib/utils/get-variant-value';
import { Image } from '@trashlab/sitesapp/src/organizations/junk-rescue/components/shared/image';
import { useThemeTextColor } from '@trashlab/sitesapp/src/organizations/junk-rescue/lib/utils/use-theme-text-color';
import { trpcPricingListSkusOpen } from '@trashlab/api/src/generated/hooks';
import { useMemo, useCallback, useState } from 'react';
import { useFilteredSkus } from '@trashlab/sitesapp/src/organizations/junk-rescue/hooks/use-filtered-skus';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

// Best Deal Dumpsters org ID
const BEST_DEAL_ORG_ID = 'd74bb19c-004b-479d-adaa-9e846d25dac7';

type VariantValue = {
  value: string;
  orderIndex?: number;
};

export const Footer = () => {
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address);
  const { textColorClass, isLightBackground } = useThemeTextColor();
  const [sizesExpanded, setSizesExpanded] = useState(false);
  const [areasExpanded, setAreasExpanded] = useState(false);

  const { filteredSkus, containerTypeOptions } = useFilteredSkus(
    website?.organization.id ?? '',
    '',
  );

  const dumpsterSizes = useMemo(() => {
    const dumpsterSkus = filteredSkus.filter(
      (sku) => sku.product?.containerType === 'DUMPSTER',
    );

    const sizes = [
      ...new Set(
        dumpsterSkus.map(
          (sku) => `${sku.product?.cubicYardage} Cubic Yard Dumpster`,
        ),
      ),
    ];

    return sizes.length > 0 ? sizes : [];
  }, [filteredSkus]);

  const hideAddressValue = useVariantValue('HIDE_ORGANIZATION_ADDRESS', 'false');
  const hideAddress = hideAddressValue.toLowerCase() === 'true';
  const city = orgAddress?.city ?? 'San Francisco';
  const state = orgAddress?.state ?? 'CA';
  const address = orgAddress?.streetAddressPartOne || '241 E. 300 S';
  const permitNumber = 'AWH#2024-00001';

  const serviceLinks = useMemo(() => {
    return containerTypeOptions.map((type) => ({
      label: `${type.label}`,
      href: `/dumpster-rentals?type=${type.value}`,
      title: `Rent a ${
        type.value === 'portableToilet' ? 'portable toilet' : 'dumpster'
      } for ${type.value} use`,
      ariaLabel: `View ${type.value} ${
        type.value === 'portableToilet' ? 'rental' : 'dumpster rental'
      } options and pricing`,
    }));
  }, [containerTypeOptions]);

  const serviceAreaNames = useVariantValue<VariantValue[]>(
    'SERVICE_AREA_NAME',
    [],
  );

  const defaultServiceAreas = ['Salt Lake City', 'Park City', 'Draper'];

  const serviceAreas = useMemo(() => {
    if (!serviceAreaNames.length) {
      return defaultServiceAreas;
    }

    const orderIndexes = [
      ...new Set(serviceAreaNames.map((area) => area.orderIndex ?? 0)),
    ].sort((a, b) => a - b);

    return orderIndexes
      .map((index) => {
        const areaName = serviceAreaNames.find(
          (area) => area.orderIndex === index,
        )?.value;
        const defaultArea = defaultServiceAreas[index];
        return areaName ?? defaultArea ?? '';
      })
      .filter((area) => area !== '');
  }, [serviceAreaNames, defaultServiceAreas]);

  const footerSections = {
    sizes: dumpsterSizes,
    services: serviceLinks,
    resources: [
      {
        label: 'About Us',
        href: '/about',
        title: 'Learn about our company',
        ariaLabel: 'Learn more about our dumpster rental company and services',
      },
      {
        label: 'Dumpster Rental FAQ',
        href: '/dumpster-faq',
        title: 'View frequently asked questions',
        ariaLabel: 'Find answers to common questions about dumpster rentals',
      },
      {
        label: 'Contact Us',
        href: '/contact-us',
        title: 'Get in touch with our team',
        ariaLabel: 'Contact our customer service team for assistance',
      },
    ],
    areas: serviceAreas,
  };

  const defaultFooterText = hideAddress
    ? `${website?.organization?.name || 'Dumpster Company'} delivers reliable,
      affordable waste management solutions. Our commitment to quality
      service and sustainability makes us a trusted choice. Thank you for
      allowing us to serve you.
      ${website?.organization?.name || 'Dumpster Company'} Serves ${city} and
      Surrounding Communities.`
    : `${website?.organization?.name || 'Dumpster Company'} delivers reliable,
      affordable waste management solutions. Our commitment to quality
      service and sustainability makes us a trusted choice. Thank you for
      allowing us to serve you.
      ${website?.organization?.name || 'Dumpster Company'} Serves ${city} and
      Surrounding Communities ${address}, ${city}, ${state}
      ${website?.organization?.name || 'Dumpster Company'} Rental ${city},${' '}
      ${state} - ${city} Waste Hauler Permit No. ${permitNumber}`;

  const footerText = useVariantValue('FOOTER_TEXT', defaultFooterText);

  const logoImage = useVariantValue('LOGO_IMAGE', '');

  const facebookLink = useVariantValue('FACEBOOK_LINK', '');
  const xLink = useVariantValue('X_LINK', '');

  // Best Deal specific contact info and hours
  const isBestDeal = website?.organization?.id === BEST_DEAL_ORG_ID;

  const bestDealAddress = useVariantValue(
    'BEST_DEAL_FOOTER_ADDRESS',
    '4621 Knopp Ave Louisville, KY 40213',
  );
  const bestDealPhone = useVariantValue('BEST_DEAL_FOOTER_PHONE', '502-396-0666');
  const bestDealEmail = useVariantValue(
    'BEST_DEAL_FOOTER_EMAIL',
    'contact@bestdealdumpsters.com',
  );
  const bestDealHoursWeekday = useVariantValue(
    'BEST_DEAL_HOURS_WEEKDAY',
    'Mon - Fri: 7:00 am - 5:00 pm',
  );
  const bestDealHoursWeekend = useVariantValue(
    'BEST_DEAL_HOURS_WEEKEND',
    'Sat - Sun: Closed',
  );

  // Best Deal social media links
  const bestDealFacebook = useVariantValue(
    'BEST_DEAL_FACEBOOK',
    'https://www.facebook.com/bestdealdumpster/',
  );
  const bestDealTwitter = useVariantValue(
    'BEST_DEAL_TWITTER',
    'https://twitter.com/BestDealDumpstr',
  );
  const bestDealYoutube = useVariantValue(
    'BEST_DEAL_YOUTUBE',
    'https://www.youtube.com/channel/UCpNSszI9YWNZS6vU4cgsb6Q',
  );
  const bestDealInstagram = useVariantValue(
    'BEST_DEAL_INSTAGRAM',
    'https://instagram.com/bestdealdumpster/',
  );
  const bestDealYelp = useVariantValue(
    'BEST_DEAL_YELP',
    'https://www.yelp.com/biz/best-deal-dumpsters-louisville-2',
  );

  const linkColorClass = isLightBackground
    ? 'text-gray-700 hover:text-black'
    : 'text-white hover:text-gray-200';
  const headingColorClass = isLightBackground
    ? 'text-gray-800'
    : 'text-slate-400';
  const iconColor = isLightBackground ? 'black' : 'white';
  const socialBtnClass = `cursor-pointer ${
    isLightBackground ? 'hover:bg-gray-300' : 'hover:bg-slate-400'
  }`;
  const separatorClass = isLightBackground ? 'bg-gray-300' : 'bg-slate-600';

  // Function to scroll to top
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const toggleSizesExpanded = useCallback(() => {
    setSizesExpanded((prev) => !prev);
  }, []);

  const toggleAreasExpanded = useCallback(() => {
    setAreasExpanded((prev) => !prev);
  }, []);

  return (
    <Container
      size="4"
      p="8"
      py="6"
      className="relative bg-[var(--background-color)]"
    >
      <IconButton
        className="absolute right-8 top-0 transform -translate-y-1/2 z-10 rounded-full bg-white border border-gray-600 shadow-md hover:shadow-lg transition-shadow w-14 h-14 flex items-center justify-center cursor-pointer"
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <ArrowUpIcon className="text-[var(--primary-color)] w-8 h-8" />
      </IconButton>

      <Grid columns="2" align="center" justify="between">
        <Box data-variable="LOGO_IMAGE">
          <Link href="/" className="mb-6 block">
            {logoImage ? (
              <Image src={logoImage} alt="logo" width={100} height={100} />
            ) : website?.organization?.logoPath ? (
              <StorageImage
                alt="logo"
                bucket="org-logos"
                logoPath={website?.organization?.logoPath}
                fit="contain"
                width={100}
                height={100}
                className="[filter:brightness(1)_drop-shadow(0.3px_0.3px_0_white)_drop-shadow(-0.3px_-0.3px_0_white)_drop-shadow(0.3px_-0.3px_0_white)_drop-shadow(-0.3px_0.3px_0_white)]"
              />
            ) : (
              <Text
                size="8"
                weight="bold"
                className="text-[var(--primary-color)] [filter:brightness(1)_drop-shadow(0.3px_0.3px_0_white)_drop-shadow(-0.3px_-0.3px_0_white)_drop-shadow(0.3px_-0.3px_0_white)_drop-shadow(-0.3px_0.3px_0_white)]"
              >
                {website?.organization?.name}
              </Text>
            )}
          </Link>
        </Box>

        <Flex direction="row" gap="4" align="center" justify="end">
          {/* Best Deal specific social links with brand colors */}
          {isBestDeal ? (
            <Flex direction="row" gap="3" align="center">
              <Text size="2" weight="bold" className={headingColorClass}>
                Follow us
              </Text>
              <a
                href={bestDealFacebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-variable="BEST_DEAL_FACEBOOK"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-[#3b5998] hover:opacity-80 transition-opacity"
              >
                <IconBrandFacebook color="white" size={20} />
              </a>
              <a
                href={bestDealTwitter}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
                data-variable="BEST_DEAL_TWITTER"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-black hover:opacity-80 transition-opacity"
              >
                <IconBrandX color="white" size={20} />
              </a>
              <a
                href={bestDealYoutube}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                data-variable="BEST_DEAL_YOUTUBE"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-[#ff0000] hover:opacity-80 transition-opacity"
              >
                <IconBrandYoutube color="white" size={20} />
              </a>
              <a
                href={`mailto:${bestDealEmail}`}
                aria-label="Email"
                data-variable="BEST_DEAL_FOOTER_EMAIL"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-[#7f7f7f] hover:opacity-80 transition-opacity"
              >
                <IconMail color="white" size={20} />
              </a>
              <a
                href={bestDealInstagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-variable="BEST_DEAL_INSTAGRAM"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-[#f58529] via-[#dd2a7b] to-[#8134af] hover:opacity-80 transition-opacity"
              >
                <IconBrandInstagram color="white" size={20} />
              </a>
              <a
                href={bestDealYelp}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Yelp"
                data-variable="BEST_DEAL_YELP"
                className="flex items-center justify-center w-8 h-8 rounded-full bg-[#d32323] hover:opacity-80 transition-opacity"
              >
                <IconYelp color="white" size={18} />
              </a>
            </Flex>
          ) : (
            <>
              <Box data-variable="FACEBOOK_LINK">
                {facebookLink && (
                  <>
                    <Text size="2" weight="bold" className={headingColorClass}>
                      Follow us on
                    </Text>
                    <IconButton
                      radius="full"
                      size="2"
                      variant="outline"
                      highContrast
                      className={socialBtnClass}
                      onClick={() => {
                        if (facebookLink) {
                          window.open(facebookLink, '_blank');
                        }
                      }}
                    >
                      <IconBrandFacebook color={iconColor} />
                    </IconButton>
                  </>
                )}
              </Box>

              <Box data-variable="X_LINK">
                {xLink && (
                  <>
                    <IconButton
                      radius="full"
                      size="2"
                      variant="outline"
                      highContrast
                      className={socialBtnClass}
                      onClick={() => {
                        if (xLink) {
                          window.open(xLink, '_blank');
                        }
                      }}
                    >
                      <IconBrandX color={iconColor} />
                    </IconButton>
                  </>
                )}
              </Box>
            </>
          )}
        </Flex>
      </Grid>

      <Separator.Root className={`SeparatorRoot ${separatorClass}`} />

      <Grid columns={{ initial: '2', sm: '5' }} my="4" gap="8">
        <Text
          size="1"
          className={`${textColorClass} col-span-2 sm:col-span-1`}
          data-variable="FOOTER_TEXT"
        >
          {footerText}
        </Text>

        <Flex direction="column" gap="2" className="col-span-1">
          <Heading as="h4" size="2" className={headingColorClass}>
            Dumpster Rental Sizes
          </Heading>
          {footerSections.sizes
            .slice(0, sizesExpanded ? footerSections.sizes.length : 5)
            .map((size, index) => (
              <Flex key={index} gap="2" align="start">
                <ExternalLinkIcon
                  className={`${
                    isLightBackground ? 'text-gray-700' : 'text-slate-400'
                  } w-3 h-3 mt-0.5`}
                />
                <Link
                  href="/dumpster-rentals"
                  className={`${linkColorClass} text-xs hover:underline`}
                >
                  {size}
                </Link>
              </Flex>
            ))}
          {footerSections.sizes.length > 5 && (
            <Flex gap="2" align="start">
              <Box className="w-3 h-3 mt-0.5 flex items-center justify-center">
                {sizesExpanded ? (
                  <ChevronUpIcon className="text-[var(--primary-color)]" />
                ) : (
                  <ChevronDownIcon className="text-[var(--primary-color)]" />
                )}
              </Box>
              <Button
                size="1"
                variant="ghost"
                onClick={toggleSizesExpanded}
                className="text-[var(--primary-color)] text-xs flex items-center justify-start p-0 m-0 h-auto min-h-0"
              >
                {sizesExpanded ? 'Show less' : 'Show more'}
              </Button>
            </Flex>
          )}
        </Flex>

        <Flex direction="column" gap="2" className="col-span-1">
          <Heading as="h4" size="2" className={headingColorClass}>
            {serviceLinks.length > 0
              ? 'Our Services'
              : 'Dumpster Rental Services'}
          </Heading>
          {footerSections.services.map((service, index) => (
            <Flex key={index} gap="2" align="start">
              <ExternalLinkIcon
                className={`${
                  isLightBackground ? 'text-gray-700' : 'text-slate-400'
                } w-3 h-3 mt-0.5`}
              />
              <Link
                href={service.href}
                className={`${linkColorClass} text-xs hover:underline`}
                title={service.title}
                aria-label={service.ariaLabel}
              >
                {service.label}
              </Link>
            </Flex>
          ))}
        </Flex>

        <Flex direction="column" gap="2" className="col-span-1">
          <Heading as="h4" size="2" className={headingColorClass}>
            Dumpster Resources
          </Heading>
          {footerSections.resources.map((resource, index) => (
            <Flex key={index} gap="2" align="start">
              <ExternalLinkIcon
                className={`${
                  isLightBackground ? 'text-gray-700' : 'text-slate-400'
                } w-3 h-3 mt-0.5`}
              />
              <Link
                href={resource.href}
                className={`${linkColorClass} text-xs hover:underline`}
                title={resource.title}
                aria-label={resource.ariaLabel}
              >
                {resource.label}
              </Link>
            </Flex>
          ))}
        </Flex>

        <Flex direction="column" gap="2" className="col-span-1">
          <Heading as="h4" size="2" className={headingColorClass}>
            Areas We Serve
          </Heading>
          {footerSections.areas
            .slice(0, areasExpanded ? footerSections.areas.length : 5)
            .map((area, index) => (
              <Flex
                key={index}
                gap="2"
                align="start"
                data-variable="SERVICE_AREA_NAME"
                data-order-index={index}
              >
                <ExternalLinkIcon
                  className={`${
                    isLightBackground ? 'text-gray-700' : 'text-slate-400'
                  } w-3 h-3 mt-0.5`}
                />
                <Link
                  href="/service-areas"
                  className={`${linkColorClass} text-xs hover:underline`}
                  title={`View dumpster rental services in ${area}`}
                  aria-label={`Learn about our dumpster rental services in ${area}`}
                >
                  {area}
                </Link>
              </Flex>
            ))}
          {footerSections.areas.length > 5 && (
            <Flex gap="2" align="start">
              <Box className="w-3 h-3 mt-0.5 flex items-center justify-center">
                {areasExpanded ? (
                  <ChevronUpIcon className="text-[var(--primary-color)]" />
                ) : (
                  <ChevronDownIcon className="text-[var(--primary-color)]" />
                )}
              </Box>
              <Button
                size="1"
                variant="ghost"
                onClick={toggleAreasExpanded}
                className="text-[var(--primary-color)] text-xs flex items-center justify-start p-0 m-0 h-auto min-h-0"
              >
                {areasExpanded ? 'Show less' : 'Show more'}
              </Button>
            </Flex>
          )}
        </Flex>

        {/* Best Deal specific: Contact Information */}
        {isBestDeal && (
          <Flex direction="column" gap="2" className="col-span-1">
            <Heading as="h4" size="2" className={headingColorClass}>
              Contact Information
            </Heading>
            <Text
              size="1"
              className={textColorClass}
              data-variable="BEST_DEAL_FOOTER_ADDRESS"
            >
              {bestDealAddress}
            </Text>
            <Flex gap="1" align="start">
              <Text size="1" className={headingColorClass}>
                Phone:
              </Text>
              <a
                href={`tel:${bestDealPhone}`}
                className={`${linkColorClass} text-xs hover:underline`}
              >
                <Text size="1" data-variable="BEST_DEAL_FOOTER_PHONE">
                  {bestDealPhone}
                </Text>
              </a>
            </Flex>
            <Flex gap="1" align="start">
              <Text size="1" className={headingColorClass}>
                Email:
              </Text>
              <a
                href={`mailto:${bestDealEmail}`}
                className={`${linkColorClass} text-xs hover:underline`}
              >
                <Text size="1" data-variable="BEST_DEAL_FOOTER_EMAIL">
                  {bestDealEmail}
                </Text>
              </a>
            </Flex>
          </Flex>
        )}

        {/* Best Deal specific: Hours of Operation */}
        {isBestDeal && (
          <Flex direction="column" gap="2" className="col-span-1">
            <Heading as="h4" size="2" className={headingColorClass}>
              Hours of Operation
            </Heading>
            <Text
              size="1"
              className={textColorClass}
              data-variable="BEST_DEAL_HOURS_WEEKDAY"
            >
              {bestDealHoursWeekday}
            </Text>
            <Text
              size="1"
              className={textColorClass}
              data-variable="BEST_DEAL_HOURS_WEEKEND"
            >
              {bestDealHoursWeekend}
            </Text>
          </Flex>
        )}
      </Grid>

      <Separator.Root className={`SeparatorRoot ${separatorClass}`} />

      <Flex direction="row" gap="4">
        <Link
          href="/privacy-policy"
          className={`${
            isLightBackground ? 'text-gray-700' : 'text-slate-400'
          } hover:underline`}
          title="Read our privacy policy"
          aria-label="View our privacy policy and data protection practices"
        >
          Privacy policy
        </Link>
        <Link
          href="/terms-and-conditions"
          className={`${
            isLightBackground ? 'text-gray-700' : 'text-slate-400'
          } hover:underline`}
          title="Read our terms and conditions"
          aria-label="View our terms of service and conditions of use"
        >
          Terms and Conditions
        </Link>
      </Flex>
    </Container>
  );
};

export default Footer;
